import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Sparkles, MapPin, BadgeCheck, ArrowRight, Star } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import BusinessLogo from "@/components/BusinessLogo";
import TierBadge from "@/components/TierBadge";
import { API } from "@/App";
import { slugForCategory } from "@/lib/categoryHeroes";
import { heroImageForCategory } from "@/lib/categoryImages";
import { resolveLogoUrl } from "@/lib/assetUrl";

export default function FeaturedPage() {
  const [items, setItems] = useState([]);
  const [curated, setCurated] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [featured, curatedRes] = await Promise.all([
          axios.get(`${API}/featured`),
          axios.get(`${API}/curated/spotlight`),
        ]);
        setItems(featured.data?.featured || []);
        setCurated(curatedRes.data?.businesses || []);
      } catch (e) {
        setItems([]);
        setCurated([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="min-h-screen bg-white" data-testid="featured-page">
      <Navbar />

      {/* Hero */}
      <section className="bg-gradient-to-br from-tbc-navy to-[#0E2541] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 text-center">
          <span className="inline-flex items-center gap-2 bg-tbc-gold/15 text-tbc-gold text-xs uppercase tracking-[0.3em] px-4 py-1.5 rounded-full">
            <Sparkles className="w-3.5 h-3.5" /> Featured this week
          </span>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl mt-6">
            Spotlight on Black-Owned Excellence
          </h1>
          <p className="text-gray-300 mt-4 max-w-2xl mx-auto">
            Premium businesses claiming the spotlight on their category landing pages.
            Discover them, support them, and pass the word.
          </p>
        </div>
      </section>

      {/* Businesses of the Week rail (curated rotation, same set as homepage) */}
      {curated.length > 0 && (
        <section className="py-10 border-b border-gray-100" data-testid="featured-botw-rail">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-end justify-between mb-6">
              <div>
                <h2 className="font-heading text-2xl sm:text-3xl text-tbc-navy">
                  <span className="text-tbc-gold">Businesses</span> of the Week
                </h2>
                <p className="text-sm text-gray-500 font-body mt-1">
                  Handpicked showcase businesses driving the community forward.
                </p>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {curated.map((b) => (
                <Link
                  key={b.business_id}
                  to={`/business/${b.business_id}`}
                  className="group bg-white rounded-xl overflow-hidden border border-gray-100 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all"
                  data-testid={`botw-rail-card-${b.business_id}`}
                >
                  <div className="aspect-square bg-tbc-cream/40 flex items-center justify-center p-2 relative">
                    {b.logo_url ? (
                      <img
                        src={resolveLogoUrl(b.logo_url)}
                        alt={b.name}
                        className="w-full h-full object-contain"
                        onError={(e) => { e.currentTarget.style.display = "none"; }}
                      />
                    ) : (
                      <BusinessLogo name={b.name} size="xl" shape="square" />
                    )}
                    <span className="absolute top-2 left-2 text-[9px] uppercase tracking-widest px-1.5 py-0.5 bg-tbc-gold text-tbc-navy rounded font-semibold">
                      This Week
                    </span>
                  </div>
                  <div className="p-3">
                    <h3 className="font-heading text-tbc-navy text-sm truncate group-hover:text-tbc-gold transition-colors">
                      {b.name}
                    </h3>
                    <p className="text-[11px] text-gray-500 truncate">{b.category}</p>
                    <div className="flex items-center justify-between mt-1">
                      {b.average_rating ? (
                        <div className="flex items-center gap-1">
                          <Star className="w-3 h-3 text-tbc-gold fill-tbc-gold" />
                          <span className="text-[11px] text-gray-600">
                            {Number(b.average_rating).toFixed(1)}
                          </span>
                        </div>
                      ) : <span />}
                      <TierBadge business={b} size="xs" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Grid */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3].map((i) => (
                <div key={i} className="bg-gray-100 rounded-xl h-72 animate-pulse" />
              ))}
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-16" data-testid="featured-page-empty">
              <Sparkles className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <h2 className="font-heading text-2xl text-tbc-navy">No featured businesses right now</h2>
              <p className="text-gray-500 mt-2 max-w-md mx-auto">
                Premium and Founding Members can claim the spotlight slot from their Dashboard.
              </p>
              <Link to="/pricing" className="inline-block mt-6">
                <span className="inline-flex items-center gap-2 text-sm bg-tbc-navy text-white px-5 py-2.5 rounded-full hover:bg-tbc-navy/90 transition-colors">
                  Become Premium <ArrowRight className="w-4 h-4" />
                </span>
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {items.map((b) => {
                const catSlug = slugForCategory(b._featured_category || b.category);
                return (
                  <Link
                    key={b.business_id}
                    to={`/business/${b.business_id}`}
                    className="group bg-white rounded-2xl overflow-hidden border border-tbc-gold/40 shadow hover:shadow-lg transition-shadow"
                    data-testid={`featured-card-${b.business_id}`}
                  >
                    <div className="aspect-video relative overflow-hidden bg-tbc-navy">
                      {/* Image fallback chain: hero image_url → logo_url → category
                          hero. Fixes the "pictures disappear after the first 4
                          businesses" issue when only the top few tiles have
                          uploaded hero images. */}
                      {(() => {
                        const src =
                          resolveLogoUrl(b.image_url) ||
                          resolveLogoUrl(b.logo_url) ||
                          heroImageForCategory(b._featured_category || b.category);
                        return src ? (
                          <img
                            src={src}
                            alt={b.name}
                            className={
                              "w-full h-full group-hover:scale-105 transition-transform duration-700 " +
                              (b.image_url ? "object-cover" : b.logo_url ? "object-contain p-6 bg-tbc-cream/40" : "object-cover")
                            }
                            loading="lazy"
                            onError={(e) => {
                              // If image_url/logo_url both broken, swap to category hero.
                              const fallback = heroImageForCategory(b._featured_category || b.category);
                              if (fallback && e.currentTarget.src !== fallback) {
                                e.currentTarget.src = fallback;
                                e.currentTarget.className = "w-full h-full object-cover group-hover:scale-105 transition-transform duration-700";
                              } else {
                                e.currentTarget.style.display = "none";
                              }
                            }}
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-tbc-gold/60">
                            <Sparkles className="w-10 h-10" />
                          </div>
                        );
                      })()}
                      <div className="absolute top-3 left-3 inline-flex items-center gap-1 bg-tbc-gold text-tbc-navy text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full">
                        <Sparkles className="w-3 h-3" /> Featured
                      </div>
                    </div>
                  <div className="p-5">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-heading text-xl text-tbc-navy group-hover:text-tbc-gold transition-colors">
                          {b.name}
                        </h3>
                        {b.is_verified && <BadgeCheck className="w-4 h-4 text-tbc-gold" />}
                        <TierBadge business={b} size="xs" />
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        {(b._featured_category || b.category)}
                      </p>
                      {b.description && (
                        <p className="text-sm text-gray-600 mt-2 line-clamp-2">{b.description}</p>
                      )}
                      <div className="flex items-center justify-between mt-3">
                        {b.city && b.state ? (
                          <span className="inline-flex items-center text-xs text-gray-400">
                            <MapPin className="w-3 h-3 mr-1" />
                            {b.city}, {b.state}
                          </span>
                        ) : <span />}
                        {catSlug && (
                          <Link
                            to={`/category/${catSlug}`}
                            onClick={(e) => e.stopPropagation()}
                            className="text-xs text-tbc-navy hover:text-tbc-gold transition-colors"
                          >
                            More in {b._featured_category || b.category} →
                          </Link>
                        )}
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
